
import React, { useState } from 'react';
import type { VoteContext } from '../governanceService';

interface VoteFormProps {
    context: VoteContext;
    onVote: (choice: string, justification: string) => Promise<void>;
}

export const VoteForm: React.FC<VoteFormProps> = ({ context, onVote }) => {
    const [choice, setChoice] = useState<string>('');
    const [justification, setJustification] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // CRITICAL VALIDATION: Minimum 20 characters
    const MIN_JUSTIFICATION_LENGTH = 20;
    const charCount = justification.trim().length;
    const isValid = choice && charCount >= MIN_JUSTIFICATION_LENGTH;
    const charColor = charCount >= MIN_JUSTIFICATION_LENGTH
        ? 'text-green-600 dark:text-green-400'
        : 'text-slate-400 dark:text-slate-500';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isValid) return;

        setSubmitting(true);
        setError('');

        try {
            await onVote(choice, justification.trim());
            // Success - parent will handle UI update
        } catch (err: any) {
            // Propagate error detail from API (409/410/400)
            const detail = err.response?.data?.detail || err.message || 'Failed to cast vote';
            setError(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Cast Your Vote
                </label>

                <div className="space-y-2">
                    {context.options.map((option) => (
                        <label
                            key={option}
                            className="flex items-center gap-2 p-3 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                        >
                            <input
                                type="radio"
                                name="vote-choice"
                                value={option}
                                checked={choice === option}
                                onChange={(e) => setChoice(e.target.value)}
                                disabled={submitting}
                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="font-mono text-sm text-slate-700 dark:text-slate-300">
                                {option}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Justification (required)
                </label>

                <textarea
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    disabled={submitting}
                    placeholder="Explain your choice (minimum 20 characters)..."
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />

                {/* Character Counter */}
                <div className={`mt-1 text-xs font-medium ${charColor}`}>
                    {charCount < MIN_JUSTIFICATION_LENGTH
                        ? `${MIN_JUSTIFICATION_LENGTH - charCount} more characters needed (${charCount}/${MIN_JUSTIFICATION_LENGTH} minimum)`
                        : `${charCount} characters`
                    }
                </div>
            </div>

            {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                    ❌ {error}
                </div>
            )}

            <button
                type="submit"
                disabled={!isValid || submitting}
                className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
            >
                {submitting ? 'Casting Vote...' : 'Cast Vote'}
            </button>
        </form>
    );
};
